import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

function fmt(d) { if (!d) return "—"; try { return new Date(d).toLocaleString(); } catch { return d; } }

const stageColor = (s = "") => ({
  "New Lead": "bg-slate-100 text-slate-700 border-slate-300",
  "Retained": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Lost": "bg-rose-50 text-rose-800 border-rose-200",
  "Dead / Unresponsive": "bg-slate-100 text-slate-500 border-slate-200",
}[s] || "bg-blue-50 text-blue-800 border-blue-200");

const Table = ({ rows, empty }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
        <th className="text-left px-3 py-2">Lead ID</th><th className="text-left px-3 py-2">Name</th>
        <th className="text-left px-3 py-2">Phone</th><th className="text-left px-3 py-2">File Type</th>
        <th className="text-left px-3 py-2">Source</th><th className="text-left px-3 py-2">Stage</th>
        <th className="text-left px-3 py-2">Next Follow-Up</th>
      </tr></thead>
      <tbody>
        {rows.map(l => (
          <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
            <td className="px-3 py-2 mono text-xs"><Link className="text-orange-600 hover:underline" to={`/leads/${l.id}`}>{l.lead_id}</Link></td>
            <td className="px-3 py-2 font-medium">{l.full_name}</td>
            <td className="px-3 py-2">{l.phone}</td>
            <td className="px-3 py-2 text-xs">{l.app_type_name || "—"}</td>
            <td className="px-3 py-2 text-xs">{l.source}</td>
            <td className="px-3 py-2"><span className={`stage-badge ${stageColor(l.stage)}`}>{l.stage}</span></td>
            <td className="px-3 py-2 text-xs">{fmt(l.next_followup_at)}</td>
          </tr>
        ))}
        {rows.length===0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">{empty}</td></tr>}
      </tbody>
    </table>
  </div>
);

export default function MyLeads() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/leads/my/dashboard").then(r=>setD(r.data)); }, []);
  if (!d) return <div className="text-slate-500">Loading…</div>;

  const cards = [
    ["New Leads", d.counts.new, "text-slate-800"],
    ["🚨 SLA Breached", d.counts.sla_breached || 0, "text-rose-700"],
    ["Due Today", d.counts.due_today, "text-amber-600"],
    ["Overdue", d.counts.overdue, "text-rose-600"],
    ["In Progress", d.counts.in_progress, "text-blue-600"],
    ["Retained (Mo)", d.counts.retained_month, "text-emerald-600"],
    ["Lost (Mo)", d.counts.lost_month, "text-rose-600"],
    ["Dead (Mo)", d.counts.dead_month, "text-slate-500"],
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">My Leads</h1>
        <p className="text-sm text-slate-500 mt-1">{d.all.length} lead(s) assigned to you</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {cards.map(([label, val, cls]) => (
          <div key={label} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="text-xs uppercase text-slate-500">{label}</div>
            <div className={`heading-font text-2xl font-bold mt-1 ${cls}`}>{val}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="new">
        <TabsList className="bg-white border border-slate-200 flex-wrap">
          <TabsTrigger value="new">New ({d.new.length})</TabsTrigger>
          <TabsTrigger value="sla_breached" data-testid="tab-sla-breached">🚨 SLA Breached ({(d.sla_breached||[]).length})</TabsTrigger>
          <TabsTrigger value="due_today">Due Today ({d.due_today.length})</TabsTrigger>
          <TabsTrigger value="overdue">Overdue ({d.overdue.length})</TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming ({d.upcoming.length})</TabsTrigger>
          <TabsTrigger value="all">All ({d.all.length})</TabsTrigger>
          <TabsTrigger value="retained">Retained ({d.retained.length})</TabsTrigger>
          <TabsTrigger value="lost">Lost ({d.lost.length})</TabsTrigger>
          <TabsTrigger value="dead">Dead ({d.dead.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="new"><Table rows={d.new} empty="No new leads."/></TabsContent>
        <TabsContent value="sla_breached"><Table rows={d.sla_breached||[]} empty="No leads have breached first-response SLA. 🎉"/></TabsContent>
        <TabsContent value="due_today"><Table rows={d.due_today} empty="No follow-ups due today. 🎉"/></TabsContent>
        <TabsContent value="overdue"><Table rows={d.overdue} empty="No overdue follow-ups."/></TabsContent>
        <TabsContent value="upcoming"><Table rows={d.upcoming} empty="Nothing scheduled upcoming."/></TabsContent>
        <TabsContent value="all"><Table rows={d.all} empty="No leads assigned to you yet."/></TabsContent>
        <TabsContent value="retained"><Table rows={d.retained} empty="No retained leads yet."/></TabsContent>
        <TabsContent value="lost"><Table rows={d.lost} empty="No lost leads."/></TabsContent>
        <TabsContent value="dead"><Table rows={d.dead} empty="No dead leads."/></TabsContent>
      </Tabs>
    </div>
  );
}
